/**
 * @module socketHandler
 * @requires EventBus
 * @since 2.16.0
 */

import { EventBus } from '../event';
import { sendError } from '../send';
import { env, glob } from '../environment';

/**
 * @constant {Symbol} SOCKET global socketHandler namespace
 */
const SOCKET = Symbol.for('SocketHandler');

/**
 * @constant {Symbol} SUBSCRIPTIONS global subscriptions namespace
 */
const SUBSCRIPTIONS = Symbol.for('SocketSubscriptions');
const ASYNC_SUBSCRIPTION_ID_PROMISE = Symbol.for('AsyncSubscriptionIdPromise');
const ASYNC_SUBSCRIPTION_ID_RESOLVER = Symbol.for('AsyncSubscriptionIdResolver');
const RECONNECT_RETRY_COUNT = Symbol.for('SocketReconnectRetryCount');
const SOCKET_STATE_READY = 1;
const SOCKET_STATE_CLOSING = 2;
const SOCKET_STATE_CLOSED = 3;
const INACTIVE_SOCKET_STATES = [SOCKET_STATE_CLOSING, SOCKET_STATE_CLOSED];
const OPEN = 'open';
const CLOSE = 'close';
const MESSAGE = 'message';
const DEFAULT_RECONNECT_MAX_RETRIES = 2;
glob[SUBSCRIPTIONS] = glob[SUBSCRIPTIONS] || new EventBus();

glob[ASYNC_SUBSCRIPTION_ID_PROMISE] = glob[ASYNC_SUBSCRIPTION_ID_PROMISE] || new Promise((resolve) => {
    glob[ASYNC_SUBSCRIPTION_ID_RESOLVER] = glob[ASYNC_SUBSCRIPTION_ID_RESOLVER] || resolve;
});

glob[RECONNECT_RETRY_COUNT] = glob[RECONNECT_RETRY_COUNT] || 0;

/**
 * Parses incoming socket event, emits message to subscribers
 * @param {Object<data>} event
 */
const incomingHandler = ({ data } = {}) => {

    if (!data) {
        return;
    }

    try {
        data = JSON.parse(data);
    } catch (_) {
        sendError('socketHandler', 'incomingHandler', `unable to parse event: ${data}`);
    }

    if (!data || typeof data !== 'object' || !data.eventType) {
        return;
    }

    glob[SUBSCRIPTIONS].emit(data.eventType, data);
};

/**
 * Splits event names by space and registers each on bus
 *
 * @param {String} method method name on bus to pass the events to
 * @param {String} events space separated event names
 * @param {Function} callback callback to run when the event triggers
 */
const passEventToBus = (method, events = '', callback) => {

    if (typeof events !== 'string') {
        return;
    }

    events.split(' ').forEach((event) => glob[SUBSCRIPTIONS][method](event, callback));
};

/**
 * Publish and subscribe to global socket events.
 * @type {Object}
 */
export const socketHandler = {

    /**
     * Connects to websocket, authenticates and handles reconnects.
     * @param {String} url The URL to connect to.
     * @param {Object} authEvent The authentication data to use.
     * @param {Object} config Configuration options (includes reconnectMaxRetries: number).
     */
    connect: (url, authEvent = {}, config = {}) => {
        const { reconnectMaxRetries = DEFAULT_RECONNECT_MAX_RETRIES } = config;

        if (!url || (glob[SOCKET] && !INACTIVE_SOCKET_STATES.includes(glob[SOCKET].readyState))|| !env.browser) {
            return socketHandler;
        }

        glob[SOCKET] = new WebSocket(url);
        glob[ASYNC_SUBSCRIPTION_ID_RESOLVER](authEvent.payload.subscription);

        glob[SOCKET].addEventListener(OPEN, () => {
            glob[RECONNECT_RETRY_COUNT] = 0;
            glob[SOCKET].addEventListener(MESSAGE, incomingHandler);
            socketHandler.send(authEvent);
        });

        glob[SOCKET].addEventListener(CLOSE, function close() {
            if (glob[RECONNECT_RETRY_COUNT] < reconnectMaxRetries) {
                glob[RECONNECT_RETRY_COUNT]++;

                // Exponential backoff: 1500ms, 3000ms...
                const backoffDelay = 1500 * Math.pow(2, glob[RECONNECT_RETRY_COUNT] - 1);

                setTimeout(() => {
                    socketHandler.connect(url, authEvent, config);
                }, backoffDelay);
            }

            glob[SOCKET].removeEventListener(CLOSE, close);
            glob[SOCKET].removeEventListener(MESSAGE, incomingHandler);
        });
        return socketHandler;
    },

    /**
     * Sends a socket event to the socket server.
     * Returns if socket is not ready yet.
     * @param {Object} payload The data to send.
     */
    send: (payload = {}) => {
        if (env.browser && glob[SOCKET].readyState === SOCKET_STATE_READY) {
            glob[SOCKET].send(JSON.stringify(payload));
        }

        return socketHandler;
    },
    on: (...args) => passEventToBus('on', ...args),
    once: (...args) => passEventToBus('once', ...args),
    off: (...args) => passEventToBus('off', ...args),
    getSubscriptionId: () => glob[ASYNC_SUBSCRIPTION_ID_PROMISE]
};
